import { ANALYTICS_EVENTS, INPUT_TYPE, MARKET } from '@nandosaus/constants';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import omit from 'lodash/omit';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { A, P, Subheading } from '../typography';
import { analytics } from '../../analytics';
import { Box } from '../grid';
import { Form } from '../form';
import { FormikCheckbox, FormikTextInput } from '../input';
import { navigation } from '../../constants';
import { useMarket } from '../../hooks/use-market';
import { useSchema } from '../../hooks/use-schema';

const CreateAccountForm = () => {
  const market = useMarket();
  const router = useRouter();
  const [error, setError] = useState();
  const [linkLoyaltyCard, setLinkLoyaltyCard] = useState(false);
  const { AuthStore } = useRootStore();
  const validationSchema = useSchema('signUp');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const initialValues = { givenName: '', familyName: '', password: '', mobile: '', email: '', sendEmail: false };

  const onSubmit = useCallback(
    async values => {
      analytics.track('User Sign Up Submitted');

      try {
        const token = await executeRecaptcha('create_account');
        await AuthStore.signUpMember(omit(values, 'checkAgree'), token);

        await analytics.track(ANALYTICS_EVENTS.USER_SIGNED_UP, {
          givenName: values.givenName,
          familyName: values.familyName,
          email: values.email,
          mobile: values.mobile,
          emailCommunication: values.sendEmail,
        });

        router.replace({
          pathname: navigation.SIGN_IN,
          query: { afterSignUp: true },
        });
      } catch (err) {
        setError(err.message);
        analytics.track('User Sign Up Failed', {
          failureReason: err.message,
        });
      }
    },
    [AuthStore, executeRecaptcha, router]
  );

  useEffect(() => {
    analytics.track('User Sign Up Clicked', { location: 'Sign Up' });
  }, []);

  return (
    <Form
      buttonText="Create Account Now"
      error={error}
      heading="New to Nando’s?"
      paragraph="We'll need some details to complete your order"
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <FormikTextInputWrapper>
        <FormikTextInput name="givenName" label="First name" />
      </FormikTextInputWrapper>
      <FormikTextInputWrapper>
        <FormikTextInput name="familyName" label="Last name" />
      </FormikTextInputWrapper>
      <FormikTextInputWrapper>
        <FormikTextInput name="email" label="Email" type={INPUT_TYPE.EMAIL} />
      </FormikTextInputWrapper>
      <FormikTextInputWrapper>
        <FormikTextInput name="mobile" label="Mobile number" type={INPUT_TYPE.PHONE} />
      </FormikTextInputWrapper>
      <FormikTextInputWrapper>
        <FormikTextInput name="password" label="Create a password" type={INPUT_TYPE.PASSWORD} />
      </FormikTextInputWrapper>
      {market === MARKET.AUSTRALIA && (
        <StyledBox mb={1} pb={1}>
          <Subheading variant={3} pb={1}>
            Already have a PERi-Perks card?
          </Subheading>
          {linkLoyaltyCard && (
            <>
              <FormikTextInputWrapper>
                <FormikTextInput name="memberNumber" label="Card Number" type={INPUT_TYPE.TEXT} />
              </FormikTextInputWrapper>
              <FormikTextInputWrapper>
                <FormikTextInput name="verifyPin" label="Verification Code" type={INPUT_TYPE.TEXT} />
              </FormikTextInputWrapper>
            </>
          )}
          <A
            variant={2}
            onClick={() => {
              setLinkLoyaltyCard(!linkLoyaltyCard);

              analytics.track('Add Peri-Perks Card Clicked');
            }}
          >
            {linkLoyaltyCard ? 'Remove Card' : 'Add a PERi-Perks card'}
          </A>
        </StyledBox>
      )}
      <StyledBox mb={1} pb={1}>
        <P variant={2} mb="0.5rem">
          All our customers are entitled to earn PERi-Perks rewards. Would you like us to send you freebies and fun
          stuff too?
        </P>
        <FormikCheckbox name="sendEmail" label={<P variant={2}>Yes Please</P>} />
      </StyledBox>
      {/* TODO: T&Cs - link & update name */}
      <Box mb={2}>
        <FormikCheckbox
          name="checkAgree"
          label={
            <P variant={2}>
              I agree to the{' '}
              <A variant={2} href={navigation.PERI_PERKS_TERMS_CONDITIONS}>
                Terms &amp; Conditions
              </A>{' '}
              &amp;{' '}
              <A variant={2} href={navigation.PRIVACY_POLICY}>
                Privacy Policy
              </A>
            </P>
          }
        />
      </Box>
    </Form>
  );
};

const CreateAccountFormContainer = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.recaptcha.siteKey}>
      <CreateAccountForm />
    </GoogleReCaptchaProvider>
  );
};

const FormikTextInputWrapper = styled(Box)`
  margin-bottom: 1rem;
`;

const StyledBox = styled(Box)`
  border-bottom: 2px solid ${({ theme }) => theme.colors.background};
`;

const ComposedCreateAccountForm = observer(CreateAccountFormContainer);
export { ComposedCreateAccountForm as CreateAccountForm };
